import React from 'react'
import { ChallengeService } from '@duck/api-definitions'
import { ArrowDownAZ, ArrowDown, ArrowUp, CircleDashed, CircleDot, ClipboardList, FlaskConical, Gauge, Hourglass, X, type LucideIcon } from 'lucide-react'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@duck/ui'
import { useTar, type TranslationKey } from '@/modules'
import { WeaponIcon } from '@/components/WeaponIcon/WeaponIcon'
import type { SortMode } from '@/hooks/useChallengeBrowser'
import {
  timeCategoryOrder,
  timeCategoryDisplayMap,
  timeCategoryIconMap,
  timeCategoryHue,
  challengeDifficultyDisplayMap,
  challengeDifficultyHue,
  challengeDifficultyOrder,
} from '@/utils/challengeData.utils'
import { ASSIGNMENTS_STATUS, type useChallengeBrowser } from '@/hooks/useChallengeBrowser'

interface RailProps { browser: ReturnType<typeof useChallengeBrowser>; onClose?: () => void; assignmentsCount?: number; sandboxesCount?: number }

const FacetRow = ({ label, count, active, onClick, leading }: {
  label: string
  count: number
  active: boolean
  onClick: () => void
  leading?: React.ReactNode
}) => (
  <button type="button" onClick={onClick}
    className={`flex w-full items-center gap-2.5 rounded-lg px-2 py-1.5 text-start ${active ? 'text-foreground' : 'text-muted-foreground'} hover:bg-muted/40`}>
    <span className={`grid h-4 w-4 flex-none place-items-center rounded border ${active ? 'border-accent bg-accent' : 'border-border'}`}>
      {active ? <span className="text-[10px] text-accent-foreground">✓</span> : null}
    </span>
    {leading !== undefined && (
      <span className="flex h-4 w-4 flex-none items-center justify-center">{leading}</span>
    )}
    <span className="flex-1 truncate text-sm">{label}</span>
    <span className="font-mono text-xs text-muted-foreground">{count}</span>
  </button>
)

const SORT_MODES: { value: SortMode; key: TranslationKey; Icon: LucideIcon }[] = [
  { value: 'difficulty', key: 'challenges.facetDifficulty', Icon: Gauge },
  { value: 'az', key: 'challenges.sortAlphabetical', Icon: ArrowDownAZ },
]

export const ChallengeFilterRail = ({ browser, onClose, assignmentsCount, sandboxesCount }: RailProps) => {
  const { t } = useTar()
  const { state, facetCounts } = browser
  const difficulties = Object.keys(challengeDifficultyOrder) as ChallengeService.ChallengeDifficultyEnum[]
  const dirLabel = t(state.sortDir === 'asc' ? 'challenges.sortAscending' : 'challenges.sortDescending')
  const statuses: [string, TranslationKey, LucideIcon][] = [
    [ChallengeService.AvailableChallengeStatusEnum.Available, 'challenges.statusGroup.available', CircleDashed],
    [ChallengeService.AvailableChallengeStatusEnum.Ongoing, 'challenges.statusGroup.ongoing', CircleDot],
    [ChallengeService.AvailableChallengeStatusEnum.Cooldown, 'challenges.statusGroup.cooldown', Hourglass],
    [ASSIGNMENTS_STATUS, 'challenges.assignmentsGroup', ClipboardList],
  ]

  return (
    <div className="w-full">
      <div className="mb-5 flex items-center gap-2">
        <h2 className="text-sm font-semibold text-foreground">{t('challenges.filters')}</h2>
        {browser.activeFilterCount > 0 && (
          <span className="rounded-full bg-accent/15 px-2 py-0.5 font-mono text-xs text-accent">{browser.activeFilterCount}</span>
        )}
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            aria-label={t('common.close')}
            className="ms-auto grid h-7 w-7 place-items-center rounded-lg text-muted-foreground hover:bg-muted/40 hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Sort — labelless icon modes + a direction toggle, ahead of the filters. */}
      <TooltipProvider>
      <div className="mb-5">
        <h3 className="mb-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">{t('challenges.sorting')}</h3>
        <div className="flex items-center gap-1.5">
          {SORT_MODES.map(({ value, key, Icon }) => {
            const active = state.sortMode === value
            return (
              <Tooltip key={value}>
                <TooltipTrigger asChild>
                  <button type="button" aria-label={t(key)} aria-pressed={active}
                    onClick={() => browser.setSortMode(value)}
                    className={`grid h-9 w-9 place-items-center rounded-lg border transition-colors ${active ? 'border-accent bg-accent/10 text-accent' : 'border-border text-muted-foreground hover:text-foreground'}`}>
                    <Icon className="h-4 w-4" />
                  </button>
                </TooltipTrigger>
                <TooltipContent>{t(key)}</TooltipContent>
              </Tooltip>
            )
          })}
          <div className="mx-0.5 h-5 w-px bg-border" />
          <Tooltip>
            <TooltipTrigger asChild>
              <button type="button" aria-label={dirLabel} onClick={browser.toggleSortDir}
                className="grid h-9 w-9 place-items-center rounded-lg border border-border text-muted-foreground transition-colors hover:text-foreground">
                {state.sortDir === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />}
              </button>
            </TooltipTrigger>
            <TooltipContent>{dirLabel}</TooltipContent>
          </Tooltip>
        </div>
      </div>
      </TooltipProvider>

      <input value={state.query} onChange={(e) => browser.setQuery(e.target.value)}
        placeholder={t('challenges.searchPlaceholder')}
        className="mb-5 w-full rounded-lg border border-border bg-card px-3 py-2 text-sm text-foreground outline-none focus:border-accent" />

      <div className="mb-5">
        <h3 className="mb-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">{t('challenges.facetDifficulty')}</h3>
        {difficulties.map((d) => (
          <FacetRow key={d} label={t(challengeDifficultyDisplayMap[d])} count={facetCounts.difficulty[d] ?? 0}
            active={state.difficulties.has(d)} onClick={() => browser.toggleDifficulty(d)}
            leading={<span className={`h-2 w-2 rounded-full ${challengeDifficultyHue[d].dot}`} />} />
        ))}
      </div>

      <div className="mb-5">
        <h3 className="mb-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">{t('challenges.facetLanguage')}</h3>
        {(['SQL', 'Python'] as const).map((w) => (
          <FacetRow key={w} label={w} count={facetCounts.weapon[w] ?? 0}
            active={state.weapons.has(w)} onClick={() => browser.toggleWeapon(w)}
            leading={<WeaponIcon weapon={w as ChallengeService.TaskWeaponsOfChoiceEnum} className="h-4 w-4" />} />
        ))}
      </div>

      <div className="mb-5">
        <h3 className="mb-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">{t('challenges.facetStatus')}</h3>
        {statuses.map(([s, key, Icon]) => (
          <FacetRow key={s} label={t(key)} count={s === ASSIGNMENTS_STATUS ? (assignmentsCount ?? 0) : 0}
            active={state.statuses.has(s)} onClick={() => browser.toggleStatus(s)}
            leading={<Icon className="h-4 w-4 text-muted-foreground" />} />
        ))}
      </div>

      {(sandboxesCount ?? 0) > 0 && (
        <div className="mb-5">
          <h3 className="mb-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">{t('challenges.sandboxesGroup')}</h3>
          <FacetRow label={t('challenges.sandboxesOnly')} count={sandboxesCount ?? 0}
            active={state.sandboxesOnly} onClick={browser.toggleSandboxesOnly}
            leading={<FlaskConical className="h-4 w-4 text-muted-foreground" />} />
        </div>
      )}

      <div className="mb-5">
        <h3 className="mb-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">{t('challenges.facetTime')}</h3>
        {timeCategoryOrder.map((tc) => {
          const Icon = timeCategoryIconMap[tc]
          return (
            <FacetRow key={tc} label={t(timeCategoryDisplayMap[tc])} count={facetCounts.time[tc] ?? 0}
              active={state.times.has(tc)} onClick={() => browser.toggleTime(tc)}
              leading={<Icon className={`h-4 w-4 ${timeCategoryHue[tc].text}`} />} />
          )
        })}
      </div>

      <button type="button" onClick={() => { browser.clearAll(); onClose?.() }}
        className="w-full rounded-lg border border-border px-3 py-2 text-sm text-muted-foreground hover:border-accent hover:text-foreground">
        {t('challenges.clearFilters')}
      </button>
    </div>
  )
}
